// Chart › Finance › Scenarios (`/c/{slug}/chart/finance/scenarios`; 11 §Planning "Scenarios",
// M5-03 `scenarios.*`): what-if questions over a month's baseline ("hire a human or a DE for this
// seat", "add two DEs to Support", "raise the team's budget"). A scenario is a list of changes;
// the API evaluates it with FIN-01's engine against today's tree and stores the result: cost,
// revenue and margin before and after, the seat lines and the budgets it changed. Restricted
// (the owner and finance admins read; finance admins create and re-evaluate). Calls on load: the
// caller's standing, then the scenarios; the form adds the roles and the org chart. ACTIONS: each
// row's hover icons edit it (the same form; saved and re-evaluated) or delete it (confirmed).
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  cn,
  EmptyState,
  Input,
  NativeSelect,
  NativeSelectOption,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FlaskConicalIcon, PencilIcon, PlusIcon, RefreshCwIcon, Trash2Icon } from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { toMinor } from "../costs/costs-page";
import { formatDateTime, formatMoney } from "../format";
import { rolesQuery } from "../queries";
import { LoadError, Loading, LoadMore, Section } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";
import {
  FinancePage,
  FormDialog,
  FormField,
  HOLDER_LABELS,
  MonthSelect,
  monthKeyOf,
  monthLabel,
  monthsAround,
  ReadOnlyNotice,
  RestrictedNotice,
  ratioText,
  useFinanceStanding,
  useOrgNames,
} from "./finance-parts";
import { financeKeys, scenariosQuery } from "./finance-queries";

type Scenario = Awaited<ReturnType<Api["client"]["scenarios"]["list"]>>["data"][number];
type Change = Scenario["changes"][number];
type Holder = "person" | "digital_employee";
type Op = Change["op"];

const OP_LABELS: Record<Op, string> = {
  add_seat: "Add seats",
  remove_seat: "Remove seats",
  change_holder: "Change who holds seats",
  change_budget: "Change a budget",
};

/** One change in words ("Add 2 Recruiter seats held by a digital employee at $900.00 each"). */
export function changeText(c: Change, currency: string, nodeName: (id: string) => string): string {
  const who = (k: Holder) => (k === "person" ? "a person" : "a digital employee");
  const seats = (n: number, t: string) => `${n} ${t} seat${n === 1 ? "" : "s"}`;
  switch (c.op) {
    case "add_seat":
      return `Add ${seats(c.count, c.seat_template)} held by ${who(c.holder_kind)} at ${formatMoney(c.assumed_cost_minor, currency)} a month each`;
    case "remove_seat":
      return `Remove ${seats(c.count, c.seat_template)} held by ${who(c.holder_kind)}`;
    case "change_holder":
      return `Move ${seats(c.count, c.seat_template)} from ${who(c.from_holder_kind)} to ${who(c.holder_kind)} at ${formatMoney(c.assumed_cost_minor, currency)} a month each`;
    case "change_budget":
      return `Set the budget of ${nodeName(c.node_id)} to ${formatMoney(c.amount_minor, currency)}`;
  }
}

export function ScenariosScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const standing = useFinanceStanding(api, company);
  const list = useInfiniteQuery({
    ...scenariosQuery(api, company.id),
    enabled: standing.restricted,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // ACTIONS: edit (re-evaluated) and delete, finance admins only, as the API.
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Scenario | null>(null);
  const [deleting, setDeleting] = useState<Scenario | null>(null);
  const remove = useMutation({
    mutationFn: (s: Scenario) =>
      api.client.scenarios.delete({ params: { co: company.id, id: s.id } }),
    onSuccess: (_d, s) => {
      setDeleting(null);
      if (selected === s.id) setSelected(null);
      toast.add({ title: `${s.name} deleted`, type: "success" });
    },
    onError: () => setDeleting(null),
    onSettled: () => qc.invalidateQueries({ queryKey: financeKeys.scenarios(company.id) }),
  });
  const shown = rows.find((r) => r.id === selected) ?? rows.at(-1) ?? null;

  return (
    <FinancePage
      company={company}
      where="chart"
      title="Scenarios"
      icon={FlaskConicalIcon}
      {...(list.data ? { count: rows.length } : {})}
      actions={
        standing.admin ? (
          <Button size="sm" onClick={() => setCreating(true)}>
            <PlusIcon aria-hidden="true" />
            New scenario
          </Button>
        ) : null
      }
    >
      {standing.query.isPending ? (
        <Loading label="Loading your finance access" rows={2} />
      ) : standing.query.isError ? (
        <LoadError what="Your finance access" error={standing.query.error} />
      ) : !standing.restricted ? (
        <RestrictedNotice company={company} what="Scenarios" />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {!standing.admin ? <ReadOnlyNotice company={company} owner={standing.owner} /> : null}
          {list.isPending ? (
            <Loading label="Loading scenarios" rows={3} />
          ) : list.isError ? (
            <LoadError what="Scenarios" error={list.error} />
          ) : rows.length === 0 ? (
            <EmptyState
              variant="card"
              icon={FlaskConicalIcon}
              title="No scenarios yet"
              description="A scenario prices a change against a month's actual numbers, without changing anything."
              actions={
                standing.admin ? (
                  <Button onClick={() => setCreating(true)}>
                    <PlusIcon aria-hidden="true" />
                    New scenario
                  </Button>
                ) : null
              }
            />
          ) : (
            <>
              <Table data-slot="scenarios">
                <TableCaption>Newest last. Pick one to see its result.</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Scenario</TableHead>
                    <TableHead scope="col">Base month</TableHead>
                    <TableHead scope="col" className="text-right">
                      Cost change
                    </TableHead>
                    <TableHead scope="col" className="text-right max-sm:hidden">
                      Margin after
                    </TableHead>
                    <TableHead scope="col" className="max-md:hidden">
                      Evaluated
                    </TableHead>
                    {standing.admin ? (
                      <TableHead scope="col">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((s) => (
                    <TableRow
                      key={s.id}
                      className="group/row"
                      data-scenario={s.id}
                      data-state={shown?.id === s.id ? "selected" : undefined}
                    >
                      <TableHead scope="row" className="font-normal">
                        <button
                          type="button"
                          className="text-left font-medium underline-offset-4 hover:underline"
                          aria-pressed={shown?.id === s.id}
                          onClick={() => setSelected(s.id)}
                        >
                          {s.name}
                        </button>
                      </TableHead>
                      <TableCell>{monthLabel(s.base_period)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {s.result ? signed(s.result.cost.delta_minor, s.result.currency) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums max-sm:hidden">
                        {s.result
                          ? formatMoney(s.result.margin.scenario_minor, s.result.currency)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-md:hidden">
                        {s.evaluated_at ? formatDateTime(s.evaluated_at) : "Not yet"}
                      </TableCell>
                      {standing.admin ? (
                        <TableCell className="w-px text-right">
                          <RowActions>
                            <RowAction
                              icon={PencilIcon}
                              label={`Edit ${s.name}`}
                              tip="Edit"
                              onClick={() => setEditing(s)}
                            />
                            <RowAction
                              icon={Trash2Icon}
                              label={`Delete ${s.name}`}
                              tip="Delete"
                              className="hover:text-danger-text"
                              onClick={() => setDeleting(s)}
                            />
                          </RowActions>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                what="scenarios"
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                onMore={() => void list.fetchNextPage()}
              />
              {shown ? (
                <ScenarioResult
                  api={api}
                  company={company}
                  scenario={shown}
                  admin={standing.admin}
                />
              ) : null}
            </>
          )}
        </div>
      )}
      {creating ? (
        <NewScenarioDialog
          api={api}
          company={company}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setSelected(id);
          }}
        />
      ) : null}
      {editing ? (
        <NewScenarioDialog
          key={editing.id}
          api={api}
          company={company}
          scenario={editing}
          onClose={() => setEditing(null)}
          onCreated={(id) => {
            setEditing(null);
            setSelected(id);
          }}
        />
      ) : null}
      <AlertDialog open={deleting !== null} onOpenChange={(o) => (o ? null : setDeleting(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              The scenario and its result go. Nothing in the company changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              Delete scenario
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FinancePage>
  );
}

const signed = (minor: number, currency: string) =>
  `${minor > 0 ? "+" : ""}${formatMoney(minor, currency)}`;

function ScenarioResult({
  api,
  company,
  scenario,
  admin,
}: {
  api: Api;
  company: CompanyAccess;
  scenario: Scenario;
  admin: boolean;
}) {
  const qc = useQueryClient();
  const names = useOrgNames(api, company);
  const evaluate = useMutation({
    mutationFn: () =>
      api.client.scenarios.evaluate({
        params: { co: company.id, id: scenario.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: financeKeys.scenarios(company.id) }),
  });
  const r = scenario.result;
  const currency = r?.currency ?? "USD";
  const money = (minor: number) => formatMoney(minor, currency);
  return (
    <Section
      id="scenario-result"
      title={scenario.name}
      description={`Against ${monthLabel(scenario.base_period)}${
        scenario.evaluated_at ? `, evaluated ${formatDateTime(scenario.evaluated_at)}` : ""
      }.`}
      actions={
        admin ? (
          <Button
            variant="secondary"
            disabled={evaluate.isPending}
            onClick={() => evaluate.mutate()}
          >
            <RefreshCwIcon aria-hidden="true" />
            {evaluate.isPending ? "Evaluating…" : "Re-evaluate"}
          </Button>
        ) : null
      }
    >
      <ul className="flex flex-col gap-1 text-sm" data-slot="scenario-changes">
        {scenario.changes.map((c, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the changes are an ordered list
          <li key={i} className="flex gap-2">
            <Badge variant="outline">{i + 1}</Badge>
            {changeText(c, currency, names.nodeName)}
          </li>
        ))}
      </ul>
      {r ? (
        <>
          <StatGrid data-slot="scenario-totals" aria-label="Scenario result">
            <StatTile label="Cost now" value={money(r.cost.base_minor)} />
            <StatTile
              label="Cost after"
              value={money(r.cost.scenario_minor)}
              detail={signed(r.cost.delta_minor, currency)}
            />
            <StatTile
              label="Margin now"
              value={money(r.margin.base_minor)}
              detail={`Revenue ${money(r.revenue_minor)}`}
            />
            <StatTile
              label="Margin after"
              value={money(r.margin.scenario_minor)}
              detail={`${ratioText(r.margin.scenario_ratio)} of revenue`}
            />
          </StatGrid>
          <Table data-slot="scenario-seats">
            <TableCaption>
              Seats after the change, by role and holder, in {currency} a month.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Role</TableHead>
                <TableHead scope="col">Held by</TableHead>
                <TableHead scope="col" className="text-right">
                  Seats
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Cost per seat
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.seats.map((s) => (
                <TableRow key={`${s.seat_template}-${s.holder_kind}-${s.cost_per_seat_minor}`}>
                  <TableHead scope="row" className="font-normal">
                    {s.seat_template}
                  </TableHead>
                  <TableCell>{HOLDER_LABELS[s.holder_kind]}</TableCell>
                  <TableCell
                    className={cn(
                      "text-right tabular-nums",
                      s.count === 0 && "text-muted-foreground",
                    )}
                  >
                    {s.count}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(s.cost_per_seat_minor)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {Object.keys(r.budgets).length ? (
            <p className="text-sm text-muted-foreground" data-slot="scenario-budgets">
              Budgets in the scenario:{" "}
              {Object.entries(r.budgets)
                .map(([id, v]) => `${names.nodeName(id)} ${money(v)}`)
                .join(" · ")}
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Not evaluated yet.</p>
      )}
    </Section>
  );
}

type Draft = {
  op: Op;
  template: string;
  holder: Holder;
  from: Holder;
  count: string;
  cost: string;
  node: string;
  amount: string;
};

const blank = (template: string, node: string): Draft => ({
  op: "add_seat",
  template,
  holder: "digital_employee",
  from: "person",
  count: "1",
  cost: "",
  node,
  amount: "",
});

/** A draft row as the contract's change, or the reason it is not one yet. */
export function draftChange(d: Draft, currency: string): Change | string {
  if (d.op === "change_budget") {
    const amount = toMinor(d.amount, currency);
    if (!d.node) return "Pick the company, a team or a seat.";
    if (amount === null || amount < 0) return "Enter the budget, like 2500.00.";
    return { op: "change_budget", node_id: d.node, amount_minor: amount };
  }
  const count = Number(d.count);
  if (!d.template) return "Pick a role.";
  if (!Number.isInteger(count) || count < 1 || count > 1000) return "Seats: 1 to 1000.";
  if (d.op === "remove_seat")
    return { op: "remove_seat", seat_template: d.template, holder_kind: d.holder, count };
  const cost = toMinor(d.cost, currency);
  if (cost === null || cost < 0) return "Enter the monthly cost per seat, like 900.00.";
  return d.op === "add_seat"
    ? {
        op: "add_seat",
        seat_template: d.template,
        holder_kind: d.holder,
        count,
        assumed_cost_minor: cost,
      }
    : {
        op: "change_holder",
        seat_template: d.template,
        from_holder_kind: d.from,
        holder_kind: d.holder,
        count,
        assumed_cost_minor: cost,
      };
}

/** ACTIONS: a saved change back as a form row (amounts in major units, two digits). */
function toDraft(c: Change, node: string): Draft {
  const major = (minor: number) => (minor / 100).toFixed(2);
  switch (c.op) {
    case "change_budget":
      return { ...blank("", c.node_id), op: c.op, amount: major(c.amount_minor) };
    case "remove_seat":
      return {
        ...blank(c.seat_template, node),
        op: c.op,
        holder: c.holder_kind,
        count: String(c.count),
      };
    case "add_seat":
      return {
        ...blank(c.seat_template, node),
        op: c.op,
        holder: c.holder_kind,
        count: String(c.count),
        cost: major(c.assumed_cost_minor),
      };
    case "change_holder":
      return {
        ...blank(c.seat_template, node),
        op: c.op,
        holder: c.holder_kind,
        from: c.from_holder_kind,
        count: String(c.count),
        cost: major(c.assumed_cost_minor),
      };
  }
}

function NewScenarioDialog({
  api,
  company,
  scenario,
  onClose,
  onCreated,
}: {
  api: Api;
  company: CompanyAccess;
  /** ACTIONS: the scenario to edit (`PATCH`, re-evaluated); none makes a new one. */
  scenario?: Scenario;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const roles = useInfiniteQuery(rolesQuery(api, company.id));
  const roleNames = [...new Set(roles.data?.pages.flatMap((p) => p.data.map((r) => r.name)) ?? [])];
  const names = useOrgNames(api, company);
  const current = monthKeyOf(new Date());
  const months = useMemo(() => {
    const around = monthsAround(current, 12, 0);
    const saved = scenario?.base_period;
    return saved && !around.includes(saved) ? [saved, ...around] : around;
  }, [current, scenario?.base_period]);
  const [name, setName] = useState(scenario?.name ?? "");
  const [base, setBase] = useState(scenario?.base_period ?? current);
  const [drafts, setDrafts] = useState<Draft[]>(
    scenario ? scenario.changes.map((c) => toDraft(c, company.id)) : [blank("", company.id)],
  );
  // Amounts are typed in the reporting currency; every supported currency has two minor digits.
  const currency = "USD";
  const ids = { name: useId(), base: useId() };
  const changes = drafts.map((d) =>
    draftChange({ ...d, template: d.template || roleNames[0] || "" }, currency),
  );
  const problems = changes.filter((c): c is string => typeof c === "string");
  const create = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), base_period: base, changes: changes as Change[] };
      return scenario
        ? api.client.scenarios.update({ params: { co: company.id, id: scenario.id }, body })
        : api.client.scenarios.create({
            params: { co: company.id },
            idempotencyKey: newIdempotencyKey(),
            body,
          });
    },
    onSuccess: async (row) => {
      await qc.invalidateQueries({ queryKey: financeKeys.scenarios(company.id) });
      onCreated(row.id);
    },
  });
  const set = (i: number, patch: Partial<Draft>) =>
    setDrafts((list) => list.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const nodes = [
    { id: company.id, name: `${company.name} (company)` },
    ...names.teams.map((t) => ({ id: t.id, name: `${t.name} (team)` })),
    ...names.seats.map((s) => ({ id: s.id, name: s.title })),
  ];

  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={scenario ? `Edit ${scenario.name}` : "New scenario"}
      description="Changes are priced in the company's reporting currency against the base month's actual numbers. Nothing in the company changes."
      submitLabel={scenario ? "Save and re-evaluate" : "Create and evaluate"}
      pending={create.isPending}
      error={create.error}
      canSubmit={name.trim().length > 0 && problems.length === 0}
      onSubmit={() => create.mutate()}
      wide
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.name} label="Name">
          <Input
            id={ids.name}
            required
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormField>
        <FormField id={ids.base} label="Base month">
          <MonthSelect id={ids.base} value={base} onChange={setBase} months={months} />
        </FormField>
      </div>
      <fieldset className="flex flex-col gap-3" data-slot="scenario-changes-form">
        <legend className="mb-2 text-sm font-medium">Changes</legend>
        {drafts.map((d, i) => (
          <ChangeRow
            // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
            key={i}
            index={i}
            draft={{ ...d, template: d.template || roleNames[0] || "" }}
            roles={roleNames}
            nodes={nodes}
            problem={typeof changes[i] === "string" ? (changes[i] as string) : null}
            onChange={(patch) => set(i, patch)}
            onRemove={
              drafts.length > 1 ? () => setDrafts((l) => l.filter((_, j) => j !== i)) : null
            }
          />
        ))}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          disabled={drafts.length >= 50}
          onClick={() => setDrafts((l) => [...l, blank(roleNames[0] ?? "", company.id)])}
        >
          <PlusIcon aria-hidden="true" />
          Add a change
        </Button>
      </fieldset>
    </FormDialog>
  );
}

function ChangeRow({
  index,
  draft: d,
  roles,
  nodes,
  problem,
  onChange,
  onRemove,
}: {
  index: number;
  draft: Draft;
  roles: string[];
  nodes: { id: string; name: string }[];
  problem: string | null;
  onChange: (patch: Partial<Draft>) => void;
  onRemove: (() => void) | null;
}) {
  const id = useId();
  const n = index + 1;
  return (
    <div className="grid gap-3 rounded-md border border-border p-3" data-change={n}>
      <div className="flex flex-wrap items-end gap-3">
        <FormField id={`${id}-op`} label={`Change ${n}`}>
          <NativeSelect
            id={`${id}-op`}
            value={d.op}
            onChange={(e) => onChange({ op: e.target.value as Op })}
          >
            {(Object.keys(OP_LABELS) as Op[]).map((op) => (
              <NativeSelectOption key={op} value={op}>
                {OP_LABELS[op]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
        {onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            aria-label={`Remove change ${n}`}
          >
            <Trash2Icon aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      {d.op === "change_budget" ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField id={`${id}-node`} label="Budget of">
            <NativeSelect
              id={`${id}-node`}
              value={d.node}
              onChange={(e) => onChange({ node: e.target.value })}
            >
              {nodes.map((x) => (
                <NativeSelectOption key={x.id} value={x.id}>
                  {x.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </FormField>
          <FormField id={`${id}-amount`} label="New monthly budget">
            <Input
              id={`${id}-amount`}
              inputMode="decimal"
              value={d.amount}
              onChange={(e) => onChange({ amount: e.target.value })}
            />
          </FormField>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            id={`${id}-role`}
            label="Role"
            hint={roles.length ? undefined : "No roles yet: add one in Setup › Roles."}
          >
            <NativeSelect
              id={`${id}-role`}
              value={d.template}
              onChange={(e) => onChange({ template: e.target.value })}
            >
              {roles.map((r) => (
                <NativeSelectOption key={r} value={r}>
                  {r}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </FormField>
          <FormField id={`${id}-count`} label="Seats">
            <Input
              id={`${id}-count`}
              type="number"
              min={1}
              max={1000}
              value={d.count}
              onChange={(e) => onChange({ count: e.target.value })}
            />
          </FormField>
          {d.op === "change_holder" ? (
            <FormField id={`${id}-from`} label="Held now by">
              <NativeSelect
                id={`${id}-from`}
                value={d.from}
                onChange={(e) => onChange({ from: e.target.value as Holder })}
              >
                <NativeSelectOption value="person">{HOLDER_LABELS.person}</NativeSelectOption>
                <NativeSelectOption value="digital_employee">
                  {HOLDER_LABELS.digital_employee}
                </NativeSelectOption>
              </NativeSelect>
            </FormField>
          ) : null}
          <FormField
            id={`${id}-holder`}
            label={d.op === "change_holder" ? "Held after by" : "Held by"}
          >
            <NativeSelect
              id={`${id}-holder`}
              value={d.holder}
              onChange={(e) => onChange({ holder: e.target.value as Holder })}
            >
              <NativeSelectOption value="person">{HOLDER_LABELS.person}</NativeSelectOption>
              <NativeSelectOption value="digital_employee">
                {HOLDER_LABELS.digital_employee}
              </NativeSelectOption>
            </NativeSelect>
          </FormField>
          {d.op !== "remove_seat" ? (
            <FormField id={`${id}-cost`} label="Monthly cost per seat">
              <Input
                id={`${id}-cost`}
                inputMode="decimal"
                value={d.cost}
                onChange={(e) => onChange({ cost: e.target.value })}
              />
            </FormField>
          ) : null}
        </div>
      )}
      {problem ? <p className="text-xs text-muted-foreground">{problem}</p> : null}
    </div>
  );
}
